// ============================================================================
// TASK MANAGEMENT MODULE - TYPE DEFINITIONS
// Enterprise-grade TypeScript interfaces for the task management system
// ============================================================================

// ============================================================================
// ENUMS
// ============================================================================

export enum UserRole {
  INDIVIDUAL_CONTRIBUTOR = 'individual_contributor',
  ENGINEER = 'engineer',
  TEAM_LEAD = 'team_lead',
  PROJECT_MANAGER = 'project_manager',
  DEPARTMENT_HEAD = 'department_head',
  ADMIN = 'admin',
  SUPER_ADMIN = 'super_admin',
  MANAGER = 'manager',
}

export enum TaskStatus {
  NOT_STARTED = 'not_started',
  IN_PROGRESS = 'in_progress',
  BLOCKED = 'blocked',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export enum TaskPriority {
  P1 = 'p1', // Critical
  P2 = 'p2', // High
  P3 = 'p3', // Medium
  P4 = 'p4', // Low
}

export enum ProjectStatus {
  PLANNING = 'planning',
  ACTIVE = 'active',
  ON_HOLD = 'on_hold',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export enum ProjectType {
  INTERNAL = 'internal',
  CUSTOMER = 'customer',
  ONSITE = 'onsite',
}

export enum ReviewStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  APPROVED = 'approved',
  COMPLETED = 'completed',
  ACKNOWLEDGED = 'acknowledged',
  REWORK_REQUIRED = 'rework_required',
  REJECTED = 'rejected',
}

export enum LeaveStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  CANCELLED = 'cancelled',
  WITHDRAWN = 'withdrawn',
}

export enum LeaveType {
  CASUAL = 'casual',
  SICK = 'sick',
  EARNED = 'earned',
  UNPAID = 'unpaid',
  COMPENSATORY = 'compensatory',
}

export enum AttendanceStatus {
  PRESENT = 'present',
  PARTIAL = 'partial',
  ABSENT = 'absent',
  LEAVE = 'leave',
  HOLIDAY = 'holiday',
}

export enum RACIRole {
  RESPONSIBLE = 'responsible',
  ACCOUNTABLE = 'accountable',
  CONSULTED = 'consulted',
  INFORMED = 'informed',
}

export enum TimerStatus {
  IDLE = 'idle',
  RUNNING = 'running',
  PAUSED = 'paused',
}

export enum KPIType {
  PERCENTAGE = 'percentage',
  NUMBER = 'number',
  CURRENCY = 'currency',
  BOOLEAN = 'boolean',
  RATING = 'rating',
}

export enum KPIFrequency {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  YEARLY = 'yearly',
}

export enum PerformanceRating {
  OUTSTANDING = 5,
  EXCEEDS_EXPECTATIONS = 4,
  MEETS_EXPECTATIONS = 3,
  NEEDS_IMPROVEMENT = 2,
  UNSATISFACTORY = 1,
}

export enum KRAStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  ON_TRACK = 'on_track',
  AT_RISK = 'at_risk',
  OVERDUE = 'overdue',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

// ============================================================================
// BASE INTERFACES
// ============================================================================

export interface BaseEntity {
  id: string;
  createdAt: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

// ============================================================================
// USER & TEAM
// ============================================================================

export interface User extends BaseEntity {
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  role: UserRole;
  departmentId: string;
  teamId?: string;
  managerId?: string;
  avatar?: string;
  isActive: boolean;
  workHoursPerDay: number; // Default: 8
  timezone: string;
  // Optional fields populated by backend or detail views
  name?: string;
  department?: string;
  joinDate?: string;
  utilizationPercent?: number;
  performanceRating?: number;
  skills?: string[];
}

export interface Team extends BaseEntity {
  name: string;
  description?: string;
  departmentId: string;
  leadId: string;
  memberIds: string[];
}

export interface Department extends BaseEntity {
  name: string;
  description?: string;
  headId: string;
  teamIds: string[];
}

// ============================================================================
// RACI ASSIGNMENT
// ============================================================================

export interface RACIAssignment {
  userId: string;
  role: RACIRole;
  assignedAt: string;
  assignedBy: string;
}

export interface TaskRACI {
  responsible: string[]; // Multiple people can be responsible
  accountable: string; // Only one person is accountable
  consulted: string[];
  informed: string[];
}

// ============================================================================
// TIME TRACKING
// ============================================================================

export interface TimeLog extends BaseEntity {
  taskId: string;
  userId: string;
  userName?: string;
  startTime: string;
  endTime?: string;
  duration: number; // in minutes
  isManual: boolean;
  manualReason?: string;
  notes?: string;
}

export interface ActiveTimer {
  taskId: string;
  taskTitle: string; // Store task title for display
  userId: string;
  startTime: string;
  status: TimerStatus;
  accumulatedTime: number; // in seconds
  pausedAt?: string;
}

export interface DailyTimeEntry {
  date: string;
  userId: string;
  totalLoggedMinutes: number;
  taskBreakdown: {
    taskId: string;
    taskTitle: string;
    minutes: number;
  }[];
}

// ============================================================================
// DUE DATE HISTORY - For tracking planned date changes
// ============================================================================

export interface DueDateChange {
  id: string;
  previousDueDate: string;
  newDueDate: string;
  reason: string;
  changedAt: string;
  changedBy: string;
  delayDays: number; // Delay from this change
}

// ============================================================================
// TASK CHECKLIST - For breaking down tasks into checkable items
// ============================================================================

export interface ChecklistItem {
  id: string;
  taskId: string;
  label: string;
  isChecked: boolean;
  checkedAt?: string;
  checkedBy?: string;
  order: number;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// RECURRING TASKS - For repeating tasks on scheduled intervals
// ============================================================================

export enum RecurrenceFrequency {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
  CUSTOM = 'custom',
}

export enum RecurrenceEndType {
  NEVER = 'never',
  AFTER = 'after',
  ON = 'on',
}

export interface RecurrenceConfig {
  id: string;
  parentTaskId: string;
  frequency: RecurrenceFrequency;
  intervalValue: number; // e.g., every 2 weeks
  daysOfWeek?: number[]; // 0 = Sunday, 6 = Saturday (for weekly)
  dayOfMonth?: number; // 1-31 (for monthly)
  monthOfYear?: number; // 1-12 (for yearly)
  endType: RecurrenceEndType;
  endValue?: string; // Date string for 'on', number string for 'after'
  nextGenerationDate?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface RecurrenceInstance {
  id: string;
  recurrenceId: string;
  taskId: string;
  instanceDate: string;
  isException: boolean; // True if this instance was modified from pattern
  createdAt: string;
}

// ============================================================================
// LINKED DOCUMENTS - Google Drive / OneDrive / Manual
// ============================================================================

export type DocumentProvider = 'google_drive' | 'onedrive' | 'manual' | 'sharepoint';

export interface LinkedDocument {
  id: string;
  name: string;
  url: string;
  provider: DocumentProvider;
  mimeType?: string;
  size?: number;
  thumbnailUrl?: string;
  linkedAt: string;
  linkedBy: string;
  externalId?: string; // ID from Google Drive / OneDrive
}

// ============================================================================
// LINKED ISSUES - Can be linked with GitHub or created as subtasks
// ============================================================================

export type IssueProvider = 'github' | 'gitlab' | 'jira' | 'internal';
export type IssueStatus = 'open' | 'in_progress' | 'resolved' | 'closed';
export type IssuePriority = 'critical' | 'high' | 'medium' | 'low';
export type IssueType = 'bug' | 'feature' | 'improvement' | 'task' | 'question';

export interface LinkedIssue {
  id: string;
  title: string;
  description?: string;
  provider: IssueProvider;
  externalId?: string; // GitHub issue number, Jira key, etc.
  externalUrl?: string;
  status: IssueStatus;
  priority: IssuePriority;
  type: IssueType;
  subtaskId?: string; // If created as a subtask
  labels: string[];
  assigneeIds: string[];
  linkedAt: string;
  linkedBy: string;
  resolvedAt?: string;
  resolvedBy?: string;
}

// ============================================================================
// LINKED COMMITS - GitHub or manual
// ============================================================================

export type CommitProvider = 'github' | 'gitlab' | 'bitbucket' | 'manual';

export interface LinkedCommit {
  id: string;
  sha: string;
  message: string;
  author: string;
  authorEmail?: string;
  authorAvatar?: string;
  provider: CommitProvider;
  repositoryName?: string;
  repositoryUrl?: string;
  commitUrl?: string;
  branch?: string;
  timestamp: string;
  linkedAt: string;
  linkedBy: string;
  additions?: number;
  deletions?: number;
  filesChanged?: number;
}

// ============================================================================
// INTEGRATIONS - GitHub, Google Drive, etc.
// ============================================================================

export interface GitHubIntegration {
  enabled: boolean;
  repositoryOwner?: string;
  repositoryName?: string;
  repositoryUrl?: string;
  defaultBranch?: string;
  connectedAt?: string;
  connectedBy?: string;
}

export interface DriveIntegration {
  enabled: boolean;
  provider?: 'google_drive' | 'onedrive';
  folderId?: string;
  folderName?: string;
  folderUrl?: string;
  connectedAt?: string;
  connectedBy?: string;
}

export interface TaskIntegrations {
  github?: GitHubIntegration;
  drive?: DriveIntegration;
}

// Task category for classification
export type TaskCategory = string;

// ============================================================================
// TASK
// ============================================================================

export interface Task extends BaseEntity {
  taskRef?: string; // Task reference like TASK-00001
  title: string;
  description?: string;
  status: TaskStatus;
  priority: TaskPriority;
  projectId?: string;
  milestoneId?: string; // Link to project milestone
  parentTaskId?: string; // For subtasks
  category?: TaskCategory; // Task classification category
  
  // RACI
  raci: TaskRACI;
  
  // Timeline
  startDate?: string;
  dueDate: string;
  completedAt?: string;
  
  // Due Date History - Track planned date changes for overdue tasks
  originalDueDate?: string; // The initial/first planned due date
  dueDateHistory?: DueDateChange[]; // History of all date changes
  totalDelayDays?: number; // Total delay from original due date
  
  // Rescheduling tracking - Track dates when task was rescheduled away
  rescheduledFromDates?: string[]; // Array of dates (YYYY-MM-DD) from which this task was rescheduled
  wasRescheduledToday?: boolean; // Quick flag for today's date
  
  // Estimates
  estimatedMinutes?: number;
  actualMinutes?: number;
  
  // Dependencies
  blockedByTaskIds: string[];
  blockingTaskIds: string[];
  dependsOnTaskIds?: string[];
  
  // Hierarchy
  subtaskIds: string[];
  order: number; // For drag-drop ordering
  
  // Metadata
  tags: string[];
  attachments: Attachment[];
  
  // Onsite Information
  isOnsite?: boolean;
  onsiteLocation?: string;
  onsiteNotes?: string;
  
  // Linked Resources
  linkedDocuments?: LinkedDocument[];
  linkedIssues?: LinkedIssue[];
  linkedCommits?: LinkedCommit[];
  
  // Integrations
  integrations?: TaskIntegrations;
  
  // Issue-specific (when task is created from an issue)
  isIssue?: boolean;
  issueType?: IssueType;
  
  // Review
  requiresReview: boolean;
  reviewStatus?: ReviewStatus;
  reviewerId?: string;
  
  // Checklist & Recurrence
  isRecurring?: boolean;
  recurrenceParentId?: string;
  recurrenceConfig?: RecurrenceConfig;
  checklistItems?: ChecklistItem[];
  checklistProgress?: {
    completed: number;
    total: number;
  };
  
  // Resolved names (populated by backend)
  raciNames?: {
    responsible: string[];
    accountable: string;
    consulted: string[];
    informed: string[];
  };
  projectName?: string;
  createdByName?: string;
  updatedByName?: string;
  reviewerName?: string;

  /** Set on today's picks when time was logged today — blocks undo of daily pick */
  workedToday?: boolean;

  /** True when this pick was auto-carried forward after a previous day's MISS */
  isCarriedForward?: boolean;

  /** Set on missed-picks payloads so the UI can group unresolved misses separately */
  missedDate?: string;
}

// ============================================================================
// DELEGATED TASK - One-on-one task assignment (not project-based, no RACI)
// ============================================================================

export enum DelegatedTaskStatus {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  REJECTED = 'rejected',
  CANCELLED = 'cancelled',
}

export interface DelegatedTaskFeedback {
  id: string;
  content: string;
  rating?: number; // 1-5 rating
  createdAt: string;
  createdBy: string;
}

export interface DelegatedTask extends BaseEntity {
  title: string;
  description?: string;
  status: DelegatedTaskStatus;
  priority: TaskPriority;
  
  // One-on-one assignment (not RACI)
  assignedTo: string; // User ID of the person the task is delegated to
  assignedBy: string; // User ID of the person who delegated the task
  assignedToName?: string; // Display name for UI
  assignedByName?: string; // Display name for UI
  
  // Timeline
  startDate?: string;
  dueDate: string;
  completedAt?: string;
  acceptedAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  
  // Progress tracking
  estimatedMinutes?: number;
  actualMinutes?: number;
  progressPercentage?: number;
  
  // Feedback & Notes
  feedback?: DelegatedTaskFeedback[];
  notes?: string; // Notes from the delegator
  completionNotes?: string; // Notes from assignee upon completion
  
  // Attachments
  attachments?: Attachment[];
  
  // Metadata
  tags?: string[];
  isUrgent?: boolean;
}

export interface TaskComment extends BaseEntity {
  taskId: string;
  content: string;
  parentCommentId?: string; // For threaded comments
  mentions: string[]; // User IDs
  createdByName?: string;
}

export interface Attachment {
  id: string;
  name: string;
  url: string;
  type: string;
  size: number;
  uploadedAt: string;
  uploadedBy: string;
}

// ============================================================================
// PROJECT
// ============================================================================

export interface Project extends BaseEntity {
  projRef?: string; // Project reference like PROJ-00001
  name: string;
  description?: string;
  status: ProjectStatus;
  priority: TaskPriority;
  projectType?: ProjectType; // Internal, Customer, or Onsite project
  
  // Hierarchy
  parentId?: string;
  childProjectIds?: string[];
  
  // Ownership
  ownerId: string;
  teamId?: string;
  departmentId?: string;
  teamMembers?: string[]; // User IDs of team members assigned to this project
  
  // Timeline
  startDate: string;
  endDate: string;
  
  // Budget/Time
  estimatedMinutes?: number;
  budgetedMinutes?: number;
  actualMinutes?: number;
  
  // Tasks
  taskIds: string[];
  /** Total task count (from API list); may be set when taskIds not populated. */
  taskCount?: number;
  /** Completed task count (from API list). */
  completedTaskCount?: number;

  // Progress
  completionPercentage: number;
  
  // Risk
  riskLevel: 'low' | 'medium' | 'high';
  riskNotes?: string;
  
  // Milestones
  milestones: Milestone[];
  
  // Metadata
  tags: string[];
  color?: string;
}

export interface Milestone {
  id: string;
  name: string;
  description?: string;
  dueDate: string;
  completedAt?: string;
  isCompleted: boolean;
  taskIds: string[];
}

// ============================================================================
// PROJECT TEMPLATES
// ============================================================================

export interface TaskTemplate {
  id: string;
  title: string;
  description?: string;
  priority: TaskPriority;
  estimatedMinutes?: number;
  order: number;
  milestoneIndex?: number; // Index of milestone this task belongs to
  tags?: string[];
}

export interface MilestoneTemplate {
  id: string;
  name: string;
  description?: string;
  daysFromStart: number; // Relative days from project start
  order: number;
}

export interface ProjectTemplate {
  id: string;
  name: string;
  description?: string;
  category: string; // e.g., "Software Development", "Marketing", "Product Launch"
  projectType?: ProjectType;
  estimatedDurationDays: number;
  milestones: MilestoneTemplate[];
  tasks: TaskTemplate[];
  tags?: string[];
  isDefault?: boolean; // System-provided template
  createdAt?: string;
  createdBy?: string;
}

// ============================================================================
// DAILY PERFORMANCE
// ============================================================================

export interface DailyPerformance extends BaseEntity {
  userId: string;
  date: string;
  
  // Tasks
  tasksPlanned: string[];
  tasksCompleted: string[];
  
  // Hit/Miss
  hitCount: number;
  missCount: number;
  hitRate: number; // percentage
  
  // Missed task reasons
  missReasons: MissReason[];
  
  // Time
  totalHoursLogged: number;
  
  // Notes
  dailyNotes?: string;
}

export interface MissReason {
  taskId: string;
  reason: string;
  category: 'blocked' | 'reprioritized' | 'underestimated' | 'external_dependency' | 'personal' | 'other';
  notes?: string;
}

// ============================================================================
// BANDWIDTH
// ============================================================================

export interface UserBandwidth {
  userId: string;
  date: string;
  
  // Hours
  totalAvailableHours: number;
  allocatedHours: number;
  loggedHours: number;
  freeHours: number;
  availableHours?: number;
  
  // Percentage
  utilizationPercentage: number;
  utilizationPercent?: number;
  isOverloaded: boolean;
  
  // Display names (populated by backend)
  userName?: string;
  name?: string;
  
  // Project breakdown (populated by detail views)
  projectBreakdown?: { projectId: string; projectName: string; hours: number }[];
  
  // Task breakdown
  taskAllocations: {
    taskId: string;
    taskTitle: string;
    allocatedHours: number;
  }[];
}

export interface WeeklyBandwidthDay {
  day: string;
  date: string;
  isToday: boolean;
  isWeekend: boolean;
  allocated: number;
  logged: number;
  available: number;
  utilization: number;
}

export interface WeeklyBandwidth {
  userId: string;
  weekStart: string;
  weekEnd: string;
  totalAvailableHours: number;
  allocatedHours: number;
  loggedHours: number;
  freeHours: number;
  utilizationPercentage: number;
  isOverloaded: boolean;
  workingDays: number;
  days: WeeklyBandwidthDay[];
}

export interface TeamBandwidth {
  teamId: string;
  date: string;
  members: UserBandwidth[];
  averageUtilization: number;
  overloadedMemberCount: number;
}

export interface TeamMemberWorkload {
  id: string;
  userId: string;
  name: string;
  email: string;
  role: UserRole;
  avatar?: string;
  tasksAssigned: number;
  tasksCompleted: number;
  activeTasks: number;
  utilizationPercentage: number;
  utilizationPercent: number; // Alias for utilizationPercentage
  hoursLogged: number;
  hoursAllocated: number;
  allocatedHours: number; // Alias for hoursAllocated
  availableHours: number;
}

// ============================================================================
// ATTENDANCE
// ============================================================================

export interface WorkBasedAttendance extends BaseEntity {
  userId: string;
  date: string;
  status: AttendanceStatus;
  
  // Inference data
  hoursLogged: number;
  tasksWorkedOn: string[];
  firstActivity?: string;
  lastActivity?: string;
  
  // Manual override
  isManualOverride: boolean;
  overrideBy?: string;
  overrideReason?: string;
  
  // Linked leave
  leaveRequestId?: string;
}

// ============================================================================
// LEAVE
// ============================================================================

export interface LeaveRequest extends BaseEntity {
  userId: string;
  type: LeaveType;
  status: LeaveStatus;
  
  // Dates
  startDate: string;
  endDate: string;
  totalDays: number;
  
  // Details
  reason: string;
  
  // Impact analysis
  affectedTaskIds: string[];
  impactAnalysis?: LeaveImpactAnalysis;
  
  // Approval
  approverId?: string;
  approvedAt?: string;
  approverComments?: string;
  
  // Delegation
  delegateTo?: string;
  
  // Resolved names (populated by backend)
  userName?: string;
  approverName?: string;
  delegateToName?: string;
}

export interface LeaveImpactAnalysis {
  tasksAtRisk: {
    taskId: string;
    taskTitle: string;
    dueDate: string;
    riskLevel: 'low' | 'medium' | 'high';
  }[];
  projectsAffected: string[];
  suggestedDelegate?: string;
  deadlineConflicts: number;
}

export interface LeaveBalance {
  userId: string;
  year: number;
  balances: {
    type: LeaveType;
    total: number;
    used: number;
    pending: number;
    remaining: number;
  }[];
}

// ============================================================================
// REVIEW
// ============================================================================

export interface Review extends BaseEntity {
  type: 'task' | 'project' | 'team' | 'individual';
  targetId: string; // Task ID, Project ID, Team ID, or User ID
  targetType: string;
  reviewType?: 'daily' | 'weekly' | 'project' | 'performance';
  
  reviewerId: string;
  revieweeId?: string; // Person being reviewed
  reviewerName?: string;
  revieweeName?: string;
  createdByName?: string;
  status: ReviewStatus;
  
  // Period (for performance reviews)
  period?: {
    startDate: string;
    endDate: string;
  };
  
  // Feedback
  comments: ReviewComment[];
  rating?: number;
  overallRating?: number;
  
  // Metrics (for performance reviews)
  metrics?: {
    name: string;
    score: number;
    weight?: number;
  }[];
  
  // Checklist
  checklist?: ReviewChecklistItem[];
  
  // History
  statusHistory: {
    status: ReviewStatus;
    changedAt: string;
    changedBy: string;
    comment?: string;
  }[];
}

export interface ReviewComment extends BaseEntity {
  reviewId: string;
  content: string;
  parentCommentId?: string;
  mentions: string[];
  attachments: Attachment[];
}

export interface ReviewChecklistItem {
  id: string;
  label: string;
  isChecked: boolean;
  checkedAt?: string;
  checkedBy?: string;
}

// ============================================================================
// NOTIFICATIONS
// ============================================================================

export interface Notification extends BaseEntity {
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  isRead: boolean;
  readAt?: string;
  
  // Link
  linkType?: 'task' | 'project' | 'review' | 'leave';
  linkId?: string;
  
  // Priority
  priority: 'low' | 'medium' | 'high';
}

export type NotificationType =
  | 'task_assigned'
  | 'task_reassigned'
  | 'task_completed'
  | 'task_overdue'
  | 'deadline_approaching'
  | 'deadline_breached'
  | 'review_requested'
  | 'review_feedback'
  | 'leave_approved'
  | 'leave_rejected'
  | 'mention'
  | 'comment';

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  errors?: string[];
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasMore: boolean;
}

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, string[]>;
}

// ============================================================================
// FILTER & SORT TYPES
// ============================================================================

export interface TaskFilters {
  status?: TaskStatus[];
  priority?: TaskPriority[];
  assigneeId?: string;
  assignedTo?: string;
  projectId?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  isOverdue?: boolean;
  search?: string;
  raciRole?: 'responsible' | 'accountable' | 'consulted' | 'informed';
}

export interface TaskSort {
  field: 'dueDate' | 'priority' | 'status' | 'createdAt' | 'title';
  direction: 'asc' | 'desc';
}

export interface ProjectFilters {
  status?: ProjectStatus[];
  ownerId?: string;
  teamId?: string;
  departmentId?: string;
  search?: string;
}

// ============================================================================
// DASHBOARD TYPES
// ============================================================================

export interface IndividualDashboardData {
  todaysTasks: Task[];
  activeTimer?: ActiveTimer;
  dailyPerformance: DailyPerformance;
  bandwidth: UserBandwidth;
  upcomingDeadlines: Task[];
  recentActivity: ActivityItem[];
  pendingReviews: Review[];
}

export interface ManagerDashboardData {
  teamMembers: User[];
  teamBandwidth: TeamBandwidth;
  missedDeadlines: Task[];
  overloadedMembers: UserBandwidth[];
  leaveConflicts: LeaveRequest[];
  projectHealth: ProjectHealthSummary[];
  pendingApprovals: {
    leaves: LeaveRequest[];
    reviews: Review[];
  };
}

export interface ProjectHealthSummary {
  projectId: string;
  projectName: string;
  progress: number;
  riskLevel: 'low' | 'medium' | 'high';
  daysRemaining: number;
  isOverdue: boolean;
  bottlenecks: string[];
}

export interface ActivityItem {
  id: string;
  type: string;
  title: string;
  description: string;
  timestamp: string;
  userId: string;
  linkType?: string;
  linkId?: string;
}

// ============================================================================
// KRA & KPI MANAGEMENT
// ============================================================================

export interface KRA extends BaseEntity {
  title: string;
  description: string;
  ownerId: string;
  ownerName?: string;
  teamId?: string;
  weight: number; // Percentage weight (0-100)
  status: KRAStatus;
  progress: number; // 0-100
  
  // Relationships
  relatedProjectIds: string[];
  relatedTaskIds: string[];
  
  // Timeline
  startDate: string;
  endDate: string;
  
  // KPIs
  kpiIds: string[];
}

export interface KPI extends BaseEntity {
  kraId: string;
  title: string;
  description?: string;
  type: KPIType;
  frequency: KPIFrequency;
  
  // Values
  targetValue: number;
  actualValue: number;
  unit?: string; // e.g., "%", "hrs", "USD", "tasks"
  
  // Measurement
  measurementCriteria: string;
  lastUpdatedAt: string;
  
  // History
  history: KPIHistoryEntry[];
}

export interface KPIHistoryEntry {
  value: number;
  timestamp: string;
  updatedBy: string;
  notes?: string;
}

export interface TeamMemberPerformance {
  userId: string;
  period: {
    startDate: string;
    endDate: string;
  };
  
  // Overall
  overallRating: PerformanceRating;
  
  // KRA/KPI Progress
  kraProgress: {
    kraId: string;
    kraTitle: string;
    progress: number;
    status: KRAStatus;
  }[];
  kpiAchievement: {
    kpiId: string;
    kpiTitle: string;
    targetValue: number;
    actualValue: number;
    achievementRate: number;
  }[];
  
  // Task Performance
  tasksCompleted: number;
  tasksAssigned: number;
  tasksOnTime: number;
  completionRate: number;
  onTimeRate: number;
  
  // Metrics
  productivity: number; // 0-100
  quality: number; // 0-100
  timeManagement: number; // 0-100
  collaboration: number; // 0-100
  
  // Achievements
  achievements: string[];
}

export interface PerformanceReview extends BaseEntity {
  userId: string;
  reviewerId: string;
  period: {
    startDate: string;
    endDate: string;
  };
  
  // Ratings
  overallRating: PerformanceRating;
  ratings: {
    technical: number; // 1-5
    communication: number;
    teamwork: number;
    leadership?: number;
    problemSolving?: number;
  };
  
  // Feedback
  strengths: string;
  improvements: string;
  goals: string;
  actionItems: string[];
  
  // Status
  status: 'draft' | 'submitted' | 'acknowledged' | 'completed';
  submittedAt?: string;
  acknowledgedAt?: string;
}

export interface TeamMemberDetail extends User {
  // Current Work
  currentProjects: {
    projectId: string;
    projectName: string;
    role: string;
    progress: number;
  }[];
  
  // RACI Ownership
  raciOwnership: {
    responsible: number;
    accountable: number;
    consulted: number;
    informed: number;
  };
  
  // Performance
  performance: TeamMemberPerformance;
  
  // KRAs
  kras: KRA[];
  
  // Recent Activities
  recentActivities: ActivityItem[];
  
  // Stats
  stats: {
    activeTasks: number;
    completedTasks: number;
    utilizationRate: number;
    currentRating: PerformanceRating;
  };
}

// ============================================================================
// CALENDAR TYPES
// ============================================================================

export interface CalendarEvent {
  id: string;
  type: 'task' | 'milestone' | 'leave' | 'holiday' | 'meeting';
  title: string;
  startDate: string;
  endDate?: string;
  isAllDay: boolean;
  color?: string;
  metadata?: Record<string, unknown>;
}

// ============================================================================
// CHART/REPORT TYPES
// ============================================================================

export interface TimeSeriesDataPoint {
  date: string;
  value: number;
  label?: string;
}

export interface WorkloadHeatmapData {
  userId: string;
  userName: string;
  days: {
    date: string;
    utilization: number;
    status: 'free' | 'optimal' | 'busy' | 'overloaded';
  }[];
}

// ============================================================================
// DEPARTMENT PERFORMANCE TRACKING
// ============================================================================

export type PerformancePeriod = 'day' | 'week' | 'month' | 'quarter' | 'year';

export interface DepartmentSummary {
  id: string;
  name: string;
  headId: string;
  headName: string;
  teamCount: number;
  memberCount: number;
  activeProjects: number;
  totalTasks: number;
  color?: string;
}

export interface DepartmentPerformance {
  departmentId: string;
  departmentName: string;
  period: {
    type: PerformancePeriod;
    startDate: string;
    endDate: string;
  };
  
  // Task Metrics
  totalTasks: number;
  completedTasks: number;
  inProgressTasks: number;
  notStartedTasks: number;
  blockedTasks: number;
  overdueTasks: number;
  completionRate: number;
  onTimeDeliveryRate: number;
  
  // Team Metrics
  totalMembers: number;
  activeMembers: number;
  avgUtilization: number;
  overloadedMembers: number;
  underutilizedMembers: number;
  
  // Performance Metrics
  avgHitRate: number;
  avgVelocity: number;
  totalEstimatedMinutes: number;
  totalActualHours: number;
  
  // Project Metrics
  activeProjects: number;
  completedProjects: number;
  projectsAtRisk: number;
  avgProjectHealth: number;
  
  // Teams within department
  teams: DepartmentTeamMetrics[];
  
  // Trend data
  trend: {
    date: string;
    completionRate: number;
    velocity: number;
    utilization: number;
  }[];
}

export interface DepartmentTeamMetrics {
  teamId: string;
  teamName: string;
  leadId: string;
  leadName: string;
  memberCount: number;
  activeTasks: number;
  completedTasks: number;
  avgPerformance: number;
  avgUtilization: number;
  velocity: number;
}

export interface DepartmentComparison {
  departments: {
    departmentId: string;
    departmentName: string;
    memberCount: number;
    completionRate: number;
    onTimeRate: number;
    utilization: number;
    velocity: number;
    hitRate: number;
    projectCount: number;
    riskLevel: 'low' | 'medium' | 'high';
  }[];
  period: PerformancePeriod;
  periodLabel: string;
}

export interface DepartmentTaskDistribution {
  departmentId: string;
  departmentName: string;
  tasksByPriority: {
    p1: number;
    p2: number;
    p3: number;
    p4: number;
  };
  tasksByStatus: {
    notStarted: number;
    inProgress: number;
    blocked: number;
    completed: number;
    cancelled: number;
  };
  tasksByCategory?: Record<string, number>;
  tasksByTeam: {
    teamId: string;
    teamName: string;
    taskCount: number;
    completedCount: number;
  }[];
}

export interface DepartmentMemberWorkload {
  departmentId: string;
  departmentName: string;
  members: {
    userId: string;
    userName: string;
    teamId: string;
    teamName: string;
    role: UserRole;
    utilizationPercent: number;
    allocatedHours: number;
    loggedHours: number;
    totalAvailableHours: number;
    activeTasks: number;
    isOverloaded: boolean;
    performanceRating: number;
  }[];
  summary: {
    totalMembers: number;
    overloaded: number;
    optimal: number;
    available: number;
    avgUtilization: number;
  };
}

// ============================================================================
// DAILY TASK COMMITMENTS & HIT/MISS TRACKING
// ============================================================================

export interface DailyCommitment extends BaseEntity {
  userId: string;
  taskId: string;
  commitmentDate: string; // Date string YYYY-MM-DD
  pickedAt: string; // ISO datetime string
  evaluatedAt?: string; // ISO datetime string
  status: 'pending' | 'hit' | 'miss';
  taskStatusAtEvaluation?: TaskStatus;
  estimatedMinutes?: number; // Snapshot of task estimated time when picked
  notes?: string;
  task?: Task; // Populated when joining with tasks table
}

export interface DailyPerformanceSummary extends BaseEntity {
  userId: string;
  date: string; // Date string YYYY-MM-DD
  totalPicked: number;
  totalHits: number;
  totalMisses: number;
  totalEstimatedMinutes: number;
  totalActualMinutes?: number;
  hitRate?: number; // Percentage 0-100
  evaluated: boolean;
}

export interface PerformanceSummary {
  dailyStats: DailyPerformanceSummary[];
  currentStreak: number; // Consecutive days with 100% hit rate
  bestStreak: number; // Best streak in the period
  averageHitRate: number; // Average hit rate across all days
  totalPicks: number;
  totalHits: number;
  totalMisses: number;
}

export interface TodaysPickedTask {
  commitmentId: string;
  taskId: string;
  commitmentDate: string;
  pickedAt: string;
  status: 'pending' | 'hit' | 'miss';
  estimatedMinutes?: number;
  task: Task;
}

